import React from "react";
import {
  ExclamationTriangleIcon,
  XCircleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

const alertConfig = {
  warning: {
    icon: ExclamationTriangleIcon,
    className: "alert-warning",
    iconColor: "text-yellow-600 dark:text-yellow-400",
  },
  error: {
    icon: XCircleIcon,
    className: "alert-error",
    iconColor: "text-red-600 dark:text-red-400",
  },
  success: {
    icon: CheckCircleIcon,
    className: "alert-success",
    iconColor: "text-green-600 dark:text-green-400",
  },
  info: {
    icon: InformationCircleIcon,
    className: "alert-info",
    iconColor: "text-link",
  },
};

/**
 * Shared Alert - the one banner component every tool page uses.
 *
 * `live` (opt-in, 2026-08-14 go-public accessibility review) : when
 * true, the Alert becomes an ARIA live region so assistive technology
 * ANNOUNCES it when it appears - role="alert" (assertive) for the
 * error and warning types because they interrupt a task the customer
 * is mid-way through, role="status" (polite) for info and success.
 * Opt-in rather than default because most Alerts on the tool pages
 * are rendered with the page (static explanatory banners) : blanket
 * live regions would make every page load chatter, while the
 * DYNAMICALLY-INSERTED Alerts (validation results, cooldown notices,
 * issuance success / failure) were previously silent to screen-reader
 * customers - the exact gap the issuance flow's Step 3 / Step 4
 * announcements close by passing live.
 */
function Alert({
  type = "info",
  title,
  children,
  onClose,
  className = "",
  live = false,
  // The heading level for `title`. Defaults to 3, which is right for an
  // Alert inside a section that already has its own h2. A PAGE-level
  // notice sitting directly under the h1 must pass 2, or the document
  // outline jumps a level with nothing at the one it skipped - see
  // SecurityNotice.jsx, which is the only caller that does.
  titleLevel = 3,
}) {
  const config = alertConfig[type] || alertConfig.info;
  const Icon = config.icon;
  // Assertive for failures the customer must act on, polite for
  // progress / outcome notices ; undefined when the Alert is static
  // page furniture (see the `live` JSDoc above).
  const liveRole = live
    ? type === "error" || type === "warning"
      ? "alert"
      : "status"
    : undefined;

  return (
    <div className={`alert ${config.className} ${className}`} role={liveRole}>
      <div className="flex">
        {/* Decorative : the type is announced through the title text
            and (when live) the role, never through the pictogram. */}
        <Icon
          aria-hidden="true"
          className={`h-5 w-5 ${config.iconColor} mr-3 shrink-0 mt-0.5`}
        />
        <div className="flex-1">
          {/*
            SAME SIZE AS THE PROSE AROUND IT (operator, 2026-08-23 : "the
            notice at the top in the yellow box looks a little strange
            because the text is not the same size as the rest of the text
            that we use"). Both were text-sm, 14px, while every page
            paragraph on this site is the 16px base - so a notice sitting
            directly above body copy read as a footnote rather than as
            something to act on. The title takes semibold now that it no
            longer has size to separate it from its own body.
          */}
          {title &&
            React.createElement(
              `h${titleLevel}`,
              { className: "font-semibold mb-1" },
              title
            )}
          <div>{children}</div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            // Accessible name (2026-08-14 go-public accessibility
            // review) : the icon-only control previously announced as
            // a bare unnamed "button", indistinguishable from any
            // other action. aria-hidden on the XMarkIcon keeps the SVG
            // out of the accessible tree so the label is the whole
            // name. The TITLE is interpolated because
            // several dismissable Alerts can be on screen at once
            // (Step 3 renders load error, save error, and cooldown
            // notice together) and identical labels left them
            // indistinguishable - the same artifact-naming fix the
            // Step 4 copy buttons received. House Title Case
            // fallback when the Alert has no title.
            aria-label={title ? `Dismiss : ${title}` : "Dismiss This Message"}
            className="ml-3 shrink-0 inline-flex rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-hidden focus:ring-2 focus:ring-gray-400"
          >
            <XMarkIcon aria-hidden="true" className="h-5 w-5 text-muted" />
          </button>
        )}
      </div>
    </div>
  );
}

export default Alert;
